"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Download, Upload, ArrowUpDown, List } from "lucide-react"
import { DATASETS } from "./data/datasets"
import { PG_SCHEMA_RUNS, PG_BASELINE_RUNS } from "./data/pg_runs_all"

import {
  runsKey,
  cacheKey,
  loadAllRuns,
  saveRuns,
  formatMetric,
} from "./utils/runsHelpers"
import { SummaryCard } from "./components/SummaryCard"
import { LogsModal } from "./components/LogsModal"
import { IterationsModal } from "./components/IterationsModal"

const datasetById = Object.fromEntries(DATASETS.map((d) => [d.id, d]))

function deltaClasses(delta) {
  const base = "text-right font-mono px-1 rounded"

  if (delta >= 0) {
    // better or equal -> green
    return [
      base,
      "bg-emerald-50 text-emerald-700",
      "dark:bg-emerald-900/40 dark:text-emerald-300",
    ].join(" ")
  }

  if (delta > -0.05) {
    // slightly worse (down to -0.05) -> yellow
    return [
      base,
      "bg-amber-50 text-amber-700",
      "dark:bg-amber-900/40 dark:text-amber-300",
    ].join(" ")
  }

  // much worse (< -0.05) -> red
  return [
    base,
    "bg-red-50 text-red-700",
    "dark:bg-red-900/40 dark:text-red-300",
  ].join(" ")
}

export default function RunsDashboardPage() {
  const [allRuns, setAllRuns] = useState([])

  const [datasetFilter, setDatasetFilter] = useState("ALL")
  const [q] = useState("")
  const [sortBy, setSortBy] = useState("ts") // "ts" | "nodesF1" | "edgesF1"
  const [sortDir, setSortDir] = useState("desc") // "asc" | "desc"

  const [logsOpen, setLogsOpen] = useState(false)
  const [logsRun, setLogsRun] = useState(null)

  const [itersOpen, setItersOpen] = useState(false)
  const [itersRun, setItersRun] = useState(null)

  // baseline comparison state (for the dashboard modal)
  const [baselineOpen, setBaselineOpen] = useState(false)
  const [baselineRun, setBaselineRun] = useState(null) // current PG-HIVE run we compare
  const [baselineChoice, setBaselineChoice] = useState("gmm") // "gmm" | "shemI"

  useEffect(() => {
    // seed/merge PG_SCHEMA_RUNS into localStorage
    try {
      Object.entries(PG_SCHEMA_RUNS).forEach(([ds, runs]) => {
        const key = runsKey(ds)
        const existing = JSON.parse(localStorage.getItem(key) || "[]")
        if (!Array.isArray(existing)) {
          localStorage.setItem(key, JSON.stringify(runs))
          return
        }
        const byId = new Map(existing.map((r) => [r.id, r]))
        runs.forEach((r) => {
          byId.set(r.id, { ...byId.get(r.id), ...r })
        })
        localStorage.setItem(key, JSON.stringify([...byId.values()]))
      })
    } catch (e) {
      console.warn("Failed to seed/merge runs from PG_SCHEMA_RUNS", e)
    }
    setAllRuns(loadAllRuns())
  }, [])

  const filtered = useMemo(() => {
    let arr = allRuns
    if (datasetFilter !== "ALL")
      arr = arr.filter((r) => r._dataset === datasetFilter)
    if (q.trim()) {
      const needle = q.toLowerCase()
      arr = arr.filter((r) =>
        JSON.stringify(r.params || {}).toLowerCase().includes(needle)
      )
    }
    const dirMul = sortDir === "asc" ? 1 : -1
    arr = [...arr]
      .sort((a, b) => {
        const A = a
        const B = b
        if (sortBy === "ts") return dirMul * ((A.ts || 0) - (B.ts || 0))
        if (sortBy === "nodesF1")
          return (
            dirMul *
            (((A.metrics?.nodes?.f1Score) || 0) -
              ((B.metrics?.nodes?.f1Score) || 0))
          )
        if (sortBy === "edgesF1")
          return (
            dirMul *
            (((A.metrics?.edges?.f1Score) || 0) -
              ((B.metrics?.edges?.f1Score) || 0))
          )
        return 0
      })
      .reverse()
    return arr
  }, [allRuns, datasetFilter, q, sortBy, sortDir])

  const totals = useMemo(() => {
    const arr =
      datasetFilter === "ALL"
        ? allRuns
        : allRuns.filter((r) => r._dataset === datasetFilter)
    const total = arr.length
    const latest = total
      ? new Date(Math.max(...arr.map((r) => r.ts || 0))).toLocaleString()
      : "—"
    const bestNodesF1 = total
      ? Math.max(...arr.map((r) => Number(r?.metrics?.nodes?.f1Score) || 0))
      : 0
    const bestEdgesF1 = total
      ? Math.max(...arr.map((r) => Number(r?.metrics?.edges?.f1Score) || 0))
      : 0
    return { total, latest, bestNodesF1, bestEdgesF1 }
  }, [allRuns, datasetFilter])

  // Overview per dataset: best PG-HIVE Nodes F1 vs GMM/ShemI baselines
  const baselineOverview = useMemo(() => {
    return DATASETS.map((ds) => {
      const id = ds.id
      const dsRuns = allRuns.filter(
        (r) => r._dataset === id && !r.kind // ignore baseline runs if any
      )
      const bestPg = dsRuns.length
        ? Math.max(
            ...dsRuns.map((r) => Number(r.metrics?.nodes?.f1Score || 0))
          )
        : 0

      const baselines = PG_BASELINE_RUNS[id] || {}
      const gmmF1 = baselines.gmm?.metrics?.nodes?.f1Score ?? null
      const shemF1 = baselines.shemI?.metrics?.nodes?.f1Score ?? null

      const maxVal = Math.max(
        bestPg || 0,
        gmmF1 || 0,
        shemF1 || 0,
        1 // avoid division by zero, also gives some minimum length
      )

      return {
        id,
        label: ds.label,
        bestPg,
        gmmF1,
        shemF1,
        maxVal,
      }
    }).filter((row) => row.bestPg || row.gmmF1 || row.shemF1)
  }, [allRuns])

  const exportAll = () => {
    const byDataset = {}
    allRuns.forEach((r) => {
      if (!byDataset[r._dataset]) byDataset[r._dataset] = []
      byDataset[r._dataset].push(r)
    })
    const blob = new Blob([JSON.stringify(byDataset, null, 2)], {
      type: "application/json",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "pg_runs_all.json"
    a.click()
    URL.revokeObjectURL(url)
  }

  const importFile = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    try {
      const text = await f.text()
      const json = JSON.parse(text)
      if (Array.isArray(json)) {
        const buckets = {}
        json.forEach((r) => {
          const ds = r._dataset || r.params?.dataset || "UNKNOWN"
          if (!buckets[ds]) buckets[ds] = []
          buckets[ds].push(r)
        })
        Object.entries(buckets).forEach(([ds, arr]) => {
          const existing = JSON.parse(localStorage.getItem(runsKey(ds)) || "[]")
          localStorage.setItem(
            runsKey(ds),
            JSON.stringify([...arr, ...existing])
          )
        })
      } else {
        Object.entries(json).forEach(([ds, arr]) => {
          const existing = JSON.parse(localStorage.getItem(runsKey(ds)) || "[]")
          localStorage.setItem(
            runsKey(ds),
            JSON.stringify([
              ...(Array.isArray(arr) ? arr : []),
              ...existing,
            ])
          )
        })
      }
      setAllRuns(loadAllRuns())
    } catch (err) {
      console.error("Import failed", err)
    } finally {
      e.target.value = ""
    }
  }

  const clearAllForDataset = (ds) => {
    if (ds === "ALL") return
    localStorage.setItem(runsKey(ds), "[]")
    setAllRuns(loadAllRuns())
  }

  const handleLoadToGraph = (run) => {
    try {
      const snapshot = run?.snapshot || {}
      const metrics = run?.metrics || {}
      const ds = run?._dataset || run?.params?.dataset || "POLE"

      const paramsForGraph = {
        ...(run.params || {}),
        dataset: ds,
      }

      const cachePayload = {
        runId: run.id,
        datasetId: ds,
        params: paramsForGraph,
        summary: run.summary || {},
        metrics,
        ts: run.ts || Date.now(),
        nodes: snapshot.nodes || [],
        edges: snapshot.edges || [],
        iterations: run.iterations || [],
        logs: run.logs || null,
      }

      localStorage.setItem(cacheKey(ds), JSON.stringify(cachePayload))

      sessionStorage.setItem(
        "pg_selected_run",
        JSON.stringify({ id: run.id, dataset: ds })
      )

      sessionStorage.setItem(
        "pg_last_run_params",
        JSON.stringify(paramsForGraph)
      )
    } catch (e) {
      console.warn("Failed to cache run for GraphInfo", e)
    }

    window.location.href = "/GraphInfo"
  }

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    else {
      setSortBy(key)
      setSortDir("desc")
    }
  }

  const openLogs = (run) => {
    setLogsRun(run)
    setLogsOpen(true)
  }
  const closeLogs = () => {
    setLogsOpen(false)
    setLogsRun(null)
  }

  const openIterations = (run) => {
    setItersRun(run)
    setItersOpen(true)
  }
  const closeIterations = () => {
    setItersOpen(false)
    setItersRun(null)
  }

  const openBaselineComparison = (run) => {
    setBaselineRun(run)
    setBaselineChoice("gmm")
    setBaselineOpen(true)
  }

  const closeBaselineComparison = () => {
    setBaselineOpen(false)
    setBaselineRun(null)
  }

  const currentMetrics = baselineRun?.metrics || null
  const currentBaselineSet = baselineRun
    ? PG_BASELINE_RUNS[baselineRun._dataset]
    : null
  const currentBaselineRun =
    currentBaselineSet && baselineChoice
      ? currentBaselineSet[baselineChoice]
      : null
  const currentBaselineMetrics = currentBaselineRun?.metrics || null

  return (
    <div className="h-screen overflow-hidden bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <div className="max-w-6xl mx-auto h-full px-4 py-4">
        <div className="h-full flex flex-col">
          {/* Scroll area – header is sticky inside */}
          <div className="">
            {/* Sticky header: controls + baseline + summary */}
            <div className=" top-0 z-10 bg-gray-50 dark:bg-gray-900 pb-3">

              {/* Dashboard Row: Baselines (left) + Summary (right) */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* LEFT 50% – Baseline table */}
                <div
                  className="border rounded-lg p-3 bg-white border-gray-200 shadow-sm
                             dark:bg-gray-800 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Datasets vs baselines (Nodes F1)
                    </h3>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      Best PG-HIVE vs GMM / ShemI
                    </span>
                  </div>

                  <p className="text-[11px] text-gray-600 dark:text-gray-300 mb-2">
                    Showing the{" "}
                    <span className="font-semibold">best PG-HIVE Nodes F1</span>{" "}
                    for each dataset, baseline F1 scores, and the{" "}
                    <span className="font-semibold">Δ vs best baseline</span>.
                  </p>

                  <div className="mt-2 overflow-x-auto">
                    <table className="min-w-full text-[11px] border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                          <th className="text-left px-2 py-1 font-medium text-gray-700 dark:text-gray-200">
                            Dataset
                          </th>
                          <th className="text-right px-2 py-1 font-medium text-gray-700 dark:text-gray-200">
                            PG-HIVE
                          </th>
                          <th className="text-right px-2 py-1 font-medium text-gray-700 dark:text-gray-200">
                            GMM
                          </th>
                          <th className="text-right px-2 py-1 font-medium text-gray-700 dark:text-gray-200">
                            ShemI
                          </th>
                          <th className="text-right px-2 py-1 font-medium text-gray-700 dark:text-gray-200">
                            Δ vs baseline
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {baselineOverview.map((row) => {
                          const hasGmm = row.gmmF1 != null
                          const hasShem = row.shemF1 != null

                          const bestBaseline =
                            hasGmm || hasShem
                              ? Math.max(
                                  hasGmm ? Number(row.gmmF1 || 0) : 0,
                                  hasShem ? Number(row.shemF1 || 0) : 0
                                )
                              : null

                          const delta =
                            bestBaseline != null
                              ? Number(row.bestPg || 0) - bestBaseline
                              : null

                          return (
                            <tr
                              key={row.id}
                              className="border-b border-gray-100 dark:border-gray-800"
                            >
                              <td className="px-2 py-1 text-xs text-gray-900 dark:text-gray-100">
                                {row.label}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                {formatMetric(row.bestPg)}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                {hasGmm ? formatMetric(row.gmmF1) : "—"}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                {hasShem ? formatMetric(row.shemF1) : "—"}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {delta == null ? (
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                    —
                                  </span>
                                ) : (
                                  <span className={deltaClasses(delta)}>
                                    {delta.toFixed(3)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* RIGHT 50% – Summary boxes */}
                <div className="grid grid-cols-2 gap-3">
                  <SummaryCard title="Total Runs" value={totals.total} />
                  <SummaryCard title="Latest Run" value={totals.latest} />
                  <SummaryCard
                    title="Best Node F1"
                    value={formatMetric(totals.bestNodesF1)}
                  />
                  <SummaryCard
                    title="Best Edge F1"
                    value={formatMetric(totals.bestEdgesF1)}
                  />
                </div>
              </div>
            </div>
              {/* Top controls */}
              <div className="flex flex-wrap items-center gap-3 pt-10">
                <select
                  value={datasetFilter}
                  onChange={(e) => setDatasetFilter(e.target.value)}
                  className="h-9 border rounded px-2 bg-white text-gray-900 border-gray-300
                             dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                >
                  <option value="ALL">All datasets</option>
                  {DATASETS.map((ds) => (
                    <option key={ds.id} value={ds.id}>
                      {ds.label} ({ds.nodes.toLocaleString()} N,{" "}
                      {ds.edges.toLocaleString()} E)
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => toggleSort("ts")}
                  className="h-9 px-3 border rounded bg-white text-gray-900 border-gray-300 flex items-center gap-1
                             hover:bg-gray-50
                             dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                >
                  <ArrowUpDown className="h-4 w-4" />
                  Date
                </button>
                <button
                  onClick={() => toggleSort("nodesF1")}
                  className="h-9 px-3 border rounded bg-white text-gray-900 border-gray-300 flex items-center gap-1
                             hover:bg-gray-50
                             dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                >
                  <ArrowUpDown className="h-4 w-4" />
                  Node F1
                </button>
                <button
                  onClick={() => toggleSort("edgesF1")}
                  className="h-9 px-3 border rounded bg-white text-gray-900 border-gray-300 flex items-center gap-1
                             hover:bg-gray-50
                             dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                >
                  <ArrowUpDown className="h-4 w-4" />
                  Edge F1
                </button>

                <div className="ml-auto flex items-center gap-2">
                  <label
                    className="h-9 px-3 border rounded bg-white text-gray-900 border-gray-300 flex items-center gap-1 cursor-pointer
                               hover:bg-gray-50
                               dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                  >
                    <Upload className="h-4 w-4" />
                    Import
                    <input
                      type="file"
                      accept="application/json"
                      className="hidden"
                      onChange={importFile}
                    />
                  </label>
                  <button
                    onClick={exportAll}
                    className="h-9 px-3 border rounded bg-white text-gray-900 border-gray-300 flex items-center gap-1
                               hover:bg-gray-50
                               dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                  >
                    <Download className="h-4 w-4" />
                    Export All
                  </button>
                  {datasetFilter !== "ALL" && (
                    <button
                      onClick={() => clearAllForDataset(datasetFilter)}
                      className="h-9 px-3 border rounded bg-red-50 text-red-700 hover:bg-red-100
                                 dark:bg-red-900/30 dark:text-red-200 dark:border dark:border-red-800 dark:hover:bg-red-900/40"
                    >
                      Clear {datasetById[datasetFilter]?.label || datasetFilter}
                    </button>
                  )}
                </div>
              </div>

            {/* Scrollable runs list */}
            <div className="mt-4 space-y-2 pb-4">
              {filtered.length === 0 ? (
                <div
                  className="text-sm text-gray-600 border rounded-md p-3 bg-white border-gray-200
                             dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700"
                >
                  No runs found
                  {datasetFilter !== "ALL"
                    ? ` for ${
                        datasetById[datasetFilter]?.label || datasetFilter
                      }`
                    : ""}
                  .
                </div>
              ) : (
                filtered.map((r) => {
                  const hasBaselines = !!PG_BASELINE_RUNS[r._dataset]
                  return (
                    <div
                      key={r.id}
                      className="border rounded-lg p-3 bg-white shadow-sm border-gray-200
                                 dark:bg-gray-800 dark:border-gray-700"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {new Date(r.ts).toLocaleString()}
                            </div>
                            <div
                              className="text-xs text-gray-600 px-2 py-0.5 border rounded bg-gray-50 border-gray-200
                                         dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700"
                            >
                              {datasetById[r._dataset]?.label || r._dataset}
                            </div>
                            {r?.params?.incremental ? (
                              <div
                                className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5
                                           dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800"
                              >
                                Incremental
                              </div>
                            ) : null}
                            {hasBaselines && !r?.params?.incremental && (
                              <div
                                className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5
                                           dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800"
                              >
                                Baselines available
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                            Params:{" "}
                            {r.params?.incremental
                              ? `INCREMENTAL ${r.params.incrementalValue}`
                              : "Full"}
                            {" • "}Adaptive:{" "}
                            {r.params?.adaptive ? "Yes" : "No"}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                            Summary: {r.summary?.nodeTypes ?? 0} node types •{" "}
                            {r.summary?.edges ?? 0} edges
                          </div>
                          {r.metrics && (
                            <div className="text-xs text-gray-700 dark:text-gray-200 mt-1">
                              <span className="font-semibold">Nodes</span>: F1{" "}
                              {formatMetric(r.metrics.nodes?.f1Score)} | P{" "}
                              {formatMetric(r.metrics.nodes?.precision)} | R{" "}
                              {formatMetric(r.metrics.nodes?.recall)}
                              <span className="mx-2">|</span>
                              <span className="font-semibold">Edges</span>: F1{" "}
                              {formatMetric(r.metrics.edges?.f1Score)} | P{" "}
                              {formatMetric(r.metrics.edges?.precision)} | R{" "}
                              {formatMetric(r.metrics.edges?.recall)}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {r?.params?.incremental && (
                            <button
                              onClick={() => openIterations(r)}
                              className="px-3 py-1 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200
                                         dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 flex items-center gap-1"
                              title="View per-iteration results"
                            >
                              <List className="h-4 w-4" />
                              Iterations
                            </button>
                          )}
                          <button
                            onClick={() => openLogs(r)}
                            className="px-3 py-1 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200
                                       dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                          >
                            Show Logs
                          </button>
                          {hasBaselines && (
                            <button
                              onClick={() => openBaselineComparison(r)}
                              className="px-3 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100
                                         dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/40 text-xs"
                              title="Compare this run with GMM / ShemI baselines"
                            >
                              Baselines
                            </button>
                          )}
                          <button
                            onClick={() => handleLoadToGraph(r)}
                            className="px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                            title="Load this run in Graph_Info"
                          >
                            Load in Graph_Info
                          </button>
                          <button
                            onClick={() => {
                              const ds =
                                r._dataset || r.params?.dataset || "dataset"
                              const blob = new Blob(
                                [JSON.stringify(r, null, 2)],
                                {
                                  type: "application/json",
                                }
                              )
                              const url = URL.createObjectURL(blob)
                              const a = document.createElement("a")
                              a.href = url
                              a.download = `${ds}_${r.id}.json`
                              a.click()
                              URL.revokeObjectURL(url)
                            }}
                            className="px-3 py-1 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200
                                       dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                          >
                            Export
                          </button>
                          <button
                            onClick={() => {
                              const ds = r._dataset
                              if (!ds) return
                              const current = JSON.parse(
                                localStorage.getItem(runsKey(ds)) || "[]"
                              )
                              const next = current.filter(
                                (x) => x.id !== r.id
                              )
                              saveRuns(ds, next)
                              setAllRuns(loadAllRuns())
                            }}
                            className="px-3 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100
                                       dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/40"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logs */}
      <LogsModal
        open={logsOpen}
        run={logsRun}
        onClose={closeLogs}
        datasetById={datasetById}
      />

      {/* Iterations */}
      <IterationsModal
        open={itersOpen}
        run={itersRun}
        onClose={closeIterations}
        datasetById={datasetById}
      />

      {/* Baseline comparison modal */}
      {baselineOpen && baselineRun && currentMetrics && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />
          <div
            className="relative z-10 w-full max-w-xl mx-4 rounded-lg border bg-white text-gray-900 border-gray-200 p-4 shadow-2xl
                       dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
          >
            {/* header */}
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h2 className="text-sm font-semibold">
                  Baseline comparison –{" "}
                  {datasetById[baselineRun._dataset]?.label ||
                    baselineRun._dataset}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Run: {new Date(baselineRun.ts).toLocaleString()}
                </p>
              </div>
              <button
                onClick={closeBaselineComparison}
                className="text-xs px-2 py-1 rounded-md border bg-gray-100 hover:bg-gray-200
                           dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>

            {!currentBaselineSet ? (
              <div className="text-xs text-red-600 dark:text-red-400">
                No baselines available for this dataset.
              </div>
            ) : (
              <div>
                {/* baseline toggle */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setBaselineChoice("gmm")}
                    className={
                      baselineChoice === "gmm"
                        ? "px-3 py-1 text-xs rounded-md bg-blue-600 text-white border-blue-600"
                        : "px-3 py-1 text-xs rounded-md border bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    }
                  >
                    GMM (nodes only)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBaselineChoice("shemI")}
                    className={
                      baselineChoice === "shemI"
                        ? "px-3 py-1 text-xs rounded-md bg-blue-600 text-white border-blue-600"
                        : "px-3 py-1 text-xs rounded-md border bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    }
                  >
                    ShemI (nodes & edges)
                  </button>
                </div>

                {!currentBaselineRun || !currentBaselineMetrics ? (
                  <div className="text-xs text-red-600 dark:text-red-400">
                    No metrics found for this baseline.
                  </div>
                ) : (
                  <div className="space-y-3 text-[11px]">
                    {/* Nodes */}
                    <div className="border rounded-md p-2 bg-white/80 dark:bg-gray-900/80 border-gray-200 dark:border-gray-700">
                      <div className="text-[10px] font-semibold mb-1 text-gray-600 dark:text-gray-400">
                        Nodes
                      </div>
                      <div className="grid grid-cols-4 gap-1 text-[10px]">
                        <div />
                        <div className="text-right font-medium">PG-HIVE</div>
                        <div className="text-right font-medium">
                          {baselineChoice === "gmm" ? "GMM" : "ShemI"}
                        </div>
                        <div className="text-right font-medium">
                          Δ (ours - baseline)
                        </div>

                        {/* F1 */}
                        <div>F1</div>
                        <div className="text-right font-mono">
                          {currentMetrics.nodes?.f1Score}
                        </div>
                        <div className="text-right font-mono">
                          {currentBaselineMetrics.nodes?.f1Score}
                        </div>
                        {(() => {
                          const delta =
                            Number(currentMetrics.nodes?.f1Score || 0) -
                            Number(currentBaselineMetrics.nodes?.f1Score || 0)
                          return (
                            <div className={deltaClasses(delta)}>
                              {delta.toFixed(3)}
                            </div>
                          )
                        })()}

                        {/* Precision */}
                        <div>Precision</div>
                        <div className="text-right font-mono">
                          {currentMetrics.nodes?.precision}
                        </div>
                        <div className="text-right font-mono">
                          {currentBaselineMetrics.nodes?.precision}
                        </div>
                        {(() => {
                          const delta =
                            Number(currentMetrics.nodes?.precision || 0) -
                            Number(currentBaselineMetrics.nodes?.precision || 0)
                          return (
                            <div className={deltaClasses(delta)}>
                              {delta.toFixed(3)}
                            </div>
                          )
                        })()}

                        {/* Recall */}
                        <div>Recall</div>
                        <div className="text-right font-mono">
                          {currentMetrics.nodes?.recall}
                        </div>
                        <div className="text-right font-mono">
                          {currentBaselineMetrics.nodes?.recall}
                        </div>
                        {(() => {
                          const delta =
                            Number(currentMetrics.nodes?.recall || 0) -
                            Number(currentBaselineMetrics.nodes?.recall || 0)
                          return (
                            <div className={deltaClasses(delta)}>
                              {delta.toFixed(3)}
                            </div>
                          )
                        })()}
                      </div>
                    </div>

                    {/* Edges – only for ShemI; GMM is node-only */}
                    {baselineChoice === "gmm" ? (
                      <div className="border rounded-md p-2 bg-white/60 dark:bg-gray-900/60 border-dashed border-gray-200 dark:border-gray-700">
                        <div className="text-[10px] font-semibold mb-1 text-gray-600 dark:text-gray-400">
                          Edges
                        </div>
                        <p className="text-[10px] text-gray-600 dark:text-gray-400">
                          GMM baseline is node-only. Edge comparison is not
                          available.
                        </p>
                      </div>
                    ) : (
                      currentBaselineMetrics.edges &&
                      currentMetrics.edges && (
                        <div className="border rounded-md p-2 bg-white/80 dark:bg-gray-900/80 border-gray-200 dark:border-gray-700">
                          <div className="text-[10px] font-semibold mb-1 text-gray-600 dark:text-gray-400">
                            Edges
                          </div>
                          <div className="grid grid-cols-4 gap-1 text-[10px]">
                            <div />
                            <div className="text-right font-medium">
                              PG-HIVE
                            </div>
                            <div className="text-right font-medium">ShemI</div>
                            <div className="text-right font-medium">
                              Δ (ours - baseline)
                            </div>

                            {/* F1 */}
                            <div>F1</div>
                            <div className="text-right font-mono">
                              {currentMetrics.edges?.f1Score}
                            </div>
                            <div className="text-right font-mono">
                              {currentBaselineMetrics.edges?.f1Score}
                            </div>
                            {(() => {
                              const delta =
                                Number(currentMetrics.edges?.f1Score || 0) -
                                Number(
                                  currentBaselineMetrics.edges?.f1Score || 0
                                )
                              return (
                                <div className={deltaClasses(delta)}>
                                  {delta.toFixed(3)}
                                </div>
                              )
                            })()}

                            {/* Precision */}
                            <div>Precision</div>
                            <div className="text-right font-mono">
                              {currentMetrics.edges?.precision}
                            </div>
                            <div className="text-right font-mono">
                              {currentBaselineMetrics.edges?.precision}
                            </div>
                            {(() => {
                              const delta =
                                Number(currentMetrics.edges?.precision || 0) -
                                Number(
                                  currentBaselineMetrics.edges?.precision || 0
                                )
                              return (
                                <div className={deltaClasses(delta)}>
                                  {delta.toFixed(3)}
                                </div>
                              )
                            })()}

                            {/* Recall */}
                            <div>Recall</div>
                            <div className="text-right font-mono">
                              {currentMetrics.edges?.recall}
                            </div>
                            <div className="text-right font-mono">
                              {currentBaselineMetrics.edges?.recall}
                            </div>
                            {(() => {
                              const delta =
                                Number(currentMetrics.edges?.recall || 0) -
                                Number(
                                  currentBaselineMetrics.edges?.recall || 0
                                )
                              return (
                                <div className={deltaClasses(delta)}>
                                  {delta.toFixed(3)}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
